import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import { PersonsByIdsDocument } from "@/graphql/generated/operations";
import { assignedPersonIds, type PlanPerson, type ProjectPackage } from "./project-plan";

// Wem kann man eine Aufgabe geben? Nur den Menschen, die im Projekt sind.
//
// Das ist keine Bequemlichkeit, sondern die Bedeutung der Zuweisung: eine
// Aufgabe an jemanden zu hängen, der mit dem Projekt nichts zu tun hat, wäre
// eine Aussage über eine fremde Person. Ein frisches Projekt hat deshalb nur
// seinen Urheber, und die Liste wächst über die Kandidaten.
//
// Zwei Sorten, ein Topf: MITGLIEDER haben Zugriff (`project.access`, mit der
// Projektabfrage geladen), KANDIDATEN sind gefragt, aber noch nicht drin. Beide
// sind zuweisbar - wer eine Aufgabe übernehmen soll, muss gefragt worden sein,
// nicht schon zugesagt haben.
//
// Eigene Abfrage macht diese Datei für die Namen: Kandidaten kommen als blosse
// Ids aus dem Dokument, und eine bereits zugewiesene Person kann das Projekt
// wieder verlassen haben. Ohne sie stünde in der Spur plötzlich kein Name mehr,
// obwohl die Zuweisung noch da ist.

export interface ProjectPeople {
  /** Auswahl für die Zuweisung: die Mitglieder des Projekts. Leer heisst leer. */
  assignable: readonly PlanPerson[];
  /** Personen mit mindestens einer Aufgabe - die Spuren des Personfokus folgen dieser Liste. */
  involved: readonly PlanPerson[];
  /** Name und Bild zu einer Zuweisung; null, wenn die Person nicht aufgelöst werden konnte. */
  lookup: (id: string | null) => PlanPerson | null;
}

export function useProjectPeople(
  packages: readonly ProjectPackage[],
  members: readonly PlanPerson[],
  candidateIds: readonly string[] = [],
): ProjectPeople {
  const assignedIds = useMemo(() => assignedPersonIds(packages), [packages]);

  const missingIds = useMemo(
    () =>
      [...new Set([...candidateIds, ...assignedIds])].filter((id) => !members.some((p) => p.id === id)),
    [assignedIds, candidateIds, members],
  );
  // Der zusammengesetzte Schlüssel hält die Abfrage ruhig: `missingIds` ist bei
  // jedem Render ein neues Array, der String ändert sich nur bei echtem Wechsel.
  const missingKey = missingIds.join(",");
  const former = useQuery(PersonsByIdsDocument, {
    variables: { input: { first: 100, where: { id: { in: missingKey ? missingKey.split(",") : [] } } } },
    skip: missingKey.length === 0,
  });

  return useMemo(() => {
    const known = new Map<string, PlanPerson>();
    for (const person of members) known.set(person.id, person);
    for (const node of former.data?.persons.nodes ?? []) {
      known.set(node.id, {
        id: node.id,
        name: node.formattedName ?? "",
        avatarContent: node.avatar?.content,
        avatarMime: node.avatar?.mimeType,
      });
    }
    const candidates = candidateIds.flatMap((id) => {
      const person = known.get(id);
      return person ? [person] : [];
    });
    return {
      // Mitglieder zuerst, Kandidaten danach: die Reihenfolge sagt, wie sicher
      // jemand dabei ist.
      assignable: [...members, ...candidates.filter((c) => !members.some((m) => m.id === c.id))],
      // Reihenfolge der Spuren folgt der Reihenfolge der Zuweisungen im Plan,
      // damit sie beim Tippen nicht springen.
      involved: assignedIds.map((id) => known.get(id) ?? { id, name: "" }),
      lookup: (id) => (id ? (known.get(id) ?? null) : null),
    };
  }, [assignedIds, candidateIds, members, former.data]);
}
